// src/features/delegations/pages/DelegationDetailPage/tabs/RevisionsTab.jsx
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Chip, Typography, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { parseUTCToLocalTimezone, getDateLocale } from '../../../../../i18n/LocalizedDateDistance';
import { useDelegations } from '../../../hooks/useDelegations';

export default function RevisionsTab({ delegationId, delegation }) {
  const { t, i18n } = useTranslation(['delegations', 'common']);
  const navigate = useNavigate();
  const { revisions, loading, error, clearError, fetchRevisionHistory } = useDelegations();

  useEffect(() => {
    fetchRevisionHistory(delegationId);
  }, [delegationId, fetchRevisionHistory]);

  const columns = useMemo(() => [
    {
      field: 'revision_number',
      headerName: t('delegations:tabs.revisions.col_number'),
      width: 190,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" fontWeight="medium">
            {t('delegations:detail.revision_number', { n: params.value })}
          </Typography>
          {params.row.is_current && (
            <Chip label={t('delegations:tabs.revisions.current')} color="primary" size="small" />
          )}
        </Box>
      ),
    },
    {
      field: 'title',
      headerName: t('delegations:list.grid.title'),
      flex: 1,
      minWidth: 160,
      renderCell: (params) =>
        params.value ? (
          <Typography variant="body2">{params.value}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            {t('delegations:detail.untitled')}
          </Typography>
        ),
    },
    {
      field: 'issued_date',
      headerName: t('delegations:create.fields.issued_date'),
      width: 170,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? format(parseUTCToLocalTimezone(params.value, i18n.language), 'dd MMM yyyy', { locale: getDateLocale(i18n.language) }) : '—'}
        </Typography>
      ),
    },
    {
      field: 'review_date',
      headerName: t('delegations:create.fields.review_date'),
      width: 170,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? format(parseUTCToLocalTimezone(params.value, i18n.language), 'dd MMM yyyy', { locale: getDateLocale(i18n.language) }) : '—'}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: t('delegations:tabs.revisions.created_at'),
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2">
          {format(parseUTCToLocalTimezone(params.value, i18n.language), 'dd MMM yyyy, HH:mm', { locale: getDateLocale(i18n.language) })}
        </Typography>
      ),
    },
    {
      field: 'signatures',
      headerName: t('delegations:detail.section.signatures'),
      minWidth: 210,
      sortable: false,
      renderCell: (params) => {
        const sigs = params.value ?? [];
        const parties = [
          { staff_member: delegation.delegator, role: 'delegator' },
          { staff_member: delegation.recipient, role: 'recipient' },
        ];
        return (
          <Box display="flex" gap={0.5} flexDirection="column" alignItems="flex-start">
            {parties.map(({ staff_member, role }) => {
              const hasSigned = sigs.some(
                (sig) => sig.staff_member.staff_member_id === staff_member.staff_member_id,
              );
              return (
                <Tooltip title={hasSigned ? t('delegations:detail.signed') : t('delegations:detail.unsigned')}>
                  <Chip
                    key={staff_member.staff_member_id}
                    label={`${staff_member.name} · ${t(`delegations:roles.${role}`)}`}
                    color={hasSigned ? 'success' : 'primary'}
                    size="small"
                    variant="outlined"
                  />
                </Tooltip>
              );
            })}
          </Box>
        );
      },
    },
  ], [t, i18n.language, delegation.delegator, delegation.recipient]);

  const rows = useMemo(
    () => [...revisions].sort((a, b) => b.revision_number - a.revision_number),
    [revisions],
  );

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" onClose={clearError}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box flex="1" minHeight="0" flexDirection="column" sx={{ p: 2 }}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.revision_id}
        loading={loading}
        pageSizeOptions={[10, 25]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        onRowClick={(params) =>
          navigate(`/delegations/${delegationId}/revisions/${params.row.revision_id}`)
        }
        disableColumnFilter
        disableColumnMenu
        disableColumnResize
        disableRowSelectionOnClick
        sx={{
          border: 'none',
          '& .MuiDataGrid-row': { cursor: 'pointer' },
        }}
      />
    </Box>
  );
}
